import React, { useCallback, useMemo, useState } from 'react';
import { FaRegStar, FaStar } from 'react-icons/fa';
import {
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Row,
  Badge,
  UncontrolledTooltip,
} from 'reactstrap';
import IconButton from '~/components/IconButton';
import {
  CANCELLED,
  DELIVERED,
  SEARCHING,
  RETURNED,
} from '~/util/deliveryStatuses';
import { formatPhone, formatPrice } from '~/util/format';
import useRoles from '~/hooks/useRoles';
import api from '~/services/api';
import history from '~/services/history';
import { getRole } from '~/util/getRole';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';
import FLEETS from '~/util/deliveryFleets';
import { getDeliveryVehicleLabel } from '~/util/deliveryVehicle';
import {
  maskCPF,
  maskEmail,
  maskPhone,
  maskBirthDate,
  maskName,
} from '~/util/mask';
import { logPiiRevealOnce } from '~/services/audit';
import CorreiosLabelDownloadButton, {
  hasCorreiosLabel,
} from '~/components/CartDetailsComponents/CorreiosLabelDownloadButton';
import DeliveryQrcode from './DeliveryQrcode';

export const defineDeliveryStatusColor = status => {
  if (status === 'Buscando entregador') {
    return 'text-warning';
  }
  if (status === 'A caminho do cliente') {
    return 'text-authorized';
  }
  if (status === 'Entregador na casa do cliente') {
    return 'text-authorized';
  }
  if (status === 'A caminho da loja') {
    return 'text-authorized';
  }
  if (status === 'Entregador na loja') {
    return 'text-authorized';
  }
  if (status === 'Entregue') {
    return 'text-success';
  }
  if (status === 'Entrega cancelada') {
    return 'text-danger';
  }
  if (status === SEARCHING) {
    return 'text-warning';
  }
  if (status === DELIVERED) {
    return 'text-success';
  }
  if (status === CANCELLED) {
    return 'text-danger';
  }
  return 'text-warning';
};

export const AntifraudViddyCard = ({ cart }) => {
  const [expanded, setExpanded] = useState(window.innerWidth > 768);

  const roles = useSelector(({ user }) => user.profile.roles);

  const role = useMemo(
    () =>
      getRole(roles, [
        'administrator',
        'digital-consultant',
        'cross-manager',
        'store-manager',
      ]),
    [roles]
  );

  const { isSupport } = useRoles();

  const BADGE_AF_VIDDY = {
    IN_FRAUD_ANALYSIS: { text: 'Em Análise', class: 'af-badge-orange' },
    ANALYSIS_FAILED: { text: 'Reprovado', class: 'af-badge-red' },
    ANALYSIS_APPROVED: { text: 'Aprovado', class: 'af-badge-green' },
    APPROVED: { text: 'Aprovado', class: 'af-badge-green' },
    REJECTED: { text: 'Reprovado', class: 'af-badge-red' },
  };

  const BADGE_AF_GATEWAY = {
    IN_FRAUD_ANALYSIS: { text: 'Em Análise', class: 'af-badge-orange' },
    approved: { text: 'Aprovado', class: 'af-badge-green' },
    reproved: { text: 'Reprovado', class: 'af-badge-red' },
  };

  const REASON_MAP = {
    rejected_antifraud: {
      label: 'rejected_antifraud',
      reason: 'Cliente com reprovação em antifraude anterior',
    },
    chargeback: {
      label: 'chargeback',
      reason: 'Cliente já teve chargeback solicitado',
    },
    pickup: {
      label: 'pickup',
      reason: 'Retirada em loja com transação acima de R$ 450',
    },
    percentile: {
      label: 'percentile',
      reason: 'Valor acima do percentil 75 da loja',
    },
    name_mismatch: {
      label: 'name_mismatch',
      reason: 'Nome do cliente difere do nome da cobrança',
    },
    cpf_mismatch: {
      label: 'cpf_mismatch',
      reason: 'CPF do cliente difere do CPF da cobrança',
    },
    email_mismatch: {
      label: 'email_mismatch',
      reason: 'E-mail do cliente difere do e-mail da cobrança',
    },
    telephone_mismatch: {
      label: 'telephone_mismatch',
      reason: 'Telefone do cliente difere do telefone da cobrança',
    },
    city_mismatch: {
      label: 'city_mismatch',
      reason: 'Cidade da loja difere da cidade da cobrança',
    },
    ddd_mismatch: {
      label: 'ddd_mismatch',
      reason: 'DDD do vendedor difere do DDD do cliente',
    },
    threeds_approved: {
      label: 'threeds_approved',
      reason: '3DS: Desafio requerido e concluído com sucesso',
    },
    fraud_code_1007: {
      label: 'fraud_code_1007',
      reason: 'Transação recusada pelo banco, código: 1007',
    },
    fraud_code_1016: {
      label: 'fraud_code_1016',
      reason: 'Saldo insuficiente',
    },
    purchase_approved_before_6: {
      label: 'purchase_approved_before_6',
      reason:
        'Compra aprovada pelo mesmo cliente em um intervalo menor do que 6 hrs',
    },
  };

  const REASON_SEVERITY = {
    chargeback: 'danger',
    rejected_antifraud: 'danger',
    percentile: 'warning',
    name_mismatch: 'warning',
    cpf_mismatch: 'warning',
    email_mismatch: 'warning',
    telephone_mismatch: 'warning',
    ddd_mismatch: 'secondary',
    city_mismatch: 'secondary',
    pickup: 'secondary',
    threeds_approved: 'success',
    fraud_code_1007: 'danger',
    fraud_code_1016: 'danger',
    purchase_approved_before_6: 'danger',
  };

  const af = cart?.anti_fraud_viddy;
  const createdAtDisplay = af?.created_at
    ? format(parseISO(af.created_at), 'dd/MM/yyyy HH:mm')
    : null;
  const statusInfoGateway = af
    ? BADGE_AF_GATEWAY[af.status_gateway] || {
        text: af.status_gateway,
        class: 'af-badge-gray',
      }
    : null;
  const statusInfo = af
    ? BADGE_AF_VIDDY[af.status] || { text: af.status, class: 'af-badge-gray' }
    : null;

  const rawReasons = Array.isArray(af?.reasons) ? af.reasons : [];
  const reasonCodes = rawReasons
    .map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') return item.id;
      return null;
    })
    .filter(Boolean);

  const groupedEntries = Object.entries(
    reasonCodes.reduce((acc, code) => {
      acc[code] = (acc[code] || 0) + 1;
      return acc;
    }, {})
  );

  const severityBgClass = sev => {
    switch (sev) {
      case 'danger':
        return 'af-badge-red';
      case 'warning':
        return 'af-badge-orange';
      case 'secondary':
        return 'af-badge-gray';
      case 'success':
        return 'af-badge-green';
      default:
        return 'af-badge-purple';
    }
  };

  const handleApproveTransaction = useCallback(async () => {
    try {
      await api.patch(`${role}/carts/${cart.id}/antifraud/approve`);
      history.push('/');
      toast.success('Pagamento aprovado com sucesso!');
    } catch (err) {
      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      }
    }
  }, [cart, role]);

  const handleRefundTransaction = useCallback(async () => {
    try {
      await api.patch(`${role}/carts/${cart.id}/antifraud/reject`);
      history.push('/');
      toast.success('Pagamento estornado com sucesso!');
    } catch (err) {
      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      }
    }
  }, [cart, role]);

  if (
    !isSupport ||
    !cart?.store?.antifraud_viddy_enabled ||
    !cart?.anti_fraud_viddy
  )
    return null;

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <Row
          className="w-100 mx-0 cursor-pointer"
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          <Col className="font-size-16 font-weight-600 px-0">
            <Row className="w-100 mx-0 justify-content-between">
              <div className="px-0 mb-0">Antifraude Viddy</div>
              <div className="px-0 mb-0">
                <IconButton
                  icon={`las la-${expanded ? 'angle-up' : 'angle-right'}`}
                  click={() => {
                    setExpanded(!expanded);
                  }}
                  color="gray-700"
                />
              </div>
            </Row>
          </Col>
        </Row>
        <Collapse isOpen={expanded}>
          {af ? (
            <>
              <div className="pt-3">
                <Row className="w-100 mx-0">
                  <Col lg={4} className="px-0 pr-lg-2 mb-3">
                    <div className="text-muted mb-1">Status</div>
                    <Badge
                      pill
                      className={`px-3 py-2 af-font-size ${statusInfo.class}`}
                    >
                      {statusInfo.text ?? 'Desconhecido'}
                    </Badge>
                  </Col>
                  {af.status_gateway && (
                    <Col lg={5} className="px-0 pr-lg-2 mb-3">
                      <div className="text-muted mb-1">Status Externo</div>
                      <Badge
                        pill
                        className={`px-3 py-2 af-font-size ${statusInfoGateway.class}`}
                      >
                        {statusInfoGateway.text ?? 'Desconhecido'}
                      </Badge>
                    </Col>
                  )}
                  {af.score > 0 && (
                    <Col lg={3} className="px-0 px-lg-2 mb-3">
                      <div className="text-muted mb-1">Score</div>
                      <div className="h4 mb-0">{af.score ?? '—'}</div>
                    </Col>
                  )}
                  <Col lg={12} className="px-0 mt-2">
                    <Row className="w-100 mx-0 mb-2 align-items-center">
                      <Col className="px-0">
                        <div className="text-muted mb-1">Motivos</div>
                      </Col>

                      {createdAtDisplay && (
                        <Col xs="auto" className="px-0 ml-auto">
                          <div className="small text-muted d-flex align-items-center">
                            <i
                              className="las la-clock mr-1"
                              aria-hidden="true"
                            />
                            <span title="Entrada em análise">
                              {createdAtDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                    </Row>
                    {groupedEntries.length === 0 ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <div className="mb-0">
                        {groupedEntries.map(([code, count]) => {
                          const raw = rawReasons.find(r =>
                            typeof r === 'object' ? r.id === code : r === code
                          );
                          const reasonText =
                            (raw && typeof raw === 'object' && raw.reason) ||
                            REASON_MAP[code]?.reason ||
                            'Motivo não identificado';

                          const sev = REASON_SEVERITY[code] || 'light';
                          const bg = severityBgClass(sev);

                          return (
                            <div
                              key={code}
                              className={`rounded px-3 py-2 mb-2 ${bg}`}
                              style={{ lineHeight: 1.0 }}
                            >
                              {reasonText}
                              {count > 1 && (
                                <span className="ms-1 small text-muted">
                                  ({count}x)
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
              {af && cart?.status_text === 'Análise de Fraude' && (
                <div className="pt-3">
                  <Button
                    color="success"
                    className="new-layout-btn main mr-lg-2 mb-2 mb-lg-0"
                    onClick={handleApproveTransaction}
                    outline
                  >
                    <i className="fa fa-check pl-1 mr-2 font-size-14" />
                    Aprovar
                  </Button>

                  <Button
                    color="danger"
                    className="new-layout-btn main ml-lg-2 mb-2 mb-lg-0"
                    onClick={handleRefundTransaction}
                    outline
                  >
                    <i className="fas fa-undo pl-1 mr-2 font-size-14" />
                    Reprovar
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="pt-3 text-muted">
              Sem dados de antifraude para este pedido.
            </div>
          )}
        </Collapse>
      </CardBody>
    </Card>
  );
};

export const AntifraudInfoCard = ({ cart }) => {
  const [expanded, setExpanded] = useState(window.innerWidth > 768);

  if (
    !cart?.anti_fraud_detail ||
    cart?.anti_fraud_detail?.status === 'approved'
  )
    return null;

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <Row
          className="w-100 mx-0 cursor-pointer"
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          <Col className="font-size-16 font-weight-600 px-0">
            <Row className="w-100 mx-0 justify-content-between">
              <div className="px-0 mb-0">Antifraude</div>
              <div className="px-0 mb-0">
                <IconButton
                  icon={`las la-${expanded ? 'angle-up' : 'angle-right'}`}
                  click={() => {
                    setExpanded(!expanded);
                  }}
                  color="gray-700"
                />
              </div>
            </Row>
          </Col>
        </Row>
        <Collapse isOpen={expanded}>
          {cart?.anti_fraud_detail && (
            <>
              {cart?.anti_fraud_detail?.status && (
                <Row className="mx-0 mt-3 mb-lg-4">
                  <Col lg={6} className="pl-0 pr-0 mb-2 mb-lg-0">
                    <div className="font-size-14 font-weight-600 text-gray-600">
                      Status
                    </div>
                    <div className="font-size-14 font-weight-400 text-gray-700">
                      {cart.anti_fraud_detail.status}
                    </div>
                  </Col>
                  <Col lg={6} className="pl-0 pr-0 mb-2 mb-lg-0">
                    <div className="font-size-14 font-weight-600 text-gray-600">
                      Provedor
                    </div>
                    <div className="font-size-14 font-weight-400 text-gray-700">
                      {cart?.anti_fraud_detail?.provider}
                    </div>
                  </Col>
                </Row>
              )}
              {cart?.anti_fraud_detail?.message && (
                <Row className="w-100 mx-0 mb-lg-4">
                  <Col lg={12} className="pl-0 pr-0 mb-0 mb-lg-0">
                    <div className="font-size-14 font-weight-600 text-gray-600">
                      Mensagem
                    </div>
                    {cart.anti_fraud_detail.message}
                  </Col>
                </Row>
              )}
            </>
          )}
        </Collapse>
      </CardBody>
    </Card>
  );
};

export const PaymentInfoCard = ({ cart }) => {
  const [expanded, setExpanded] = useState(window.innerWidth > 768);
  const roles = useSelector(({ user }) => user.profile.roles);
  const role = useMemo(
    () =>
      getRole(roles, [
        'administrator',
        'digital-consultant',
        'cross-manager',
        'store-manager',
      ]),
    [roles]
  );

  const isLinkPayment =
    (cart?.payment_type === 105 ||
      cart?.payment_type === 106 ||
      cart?.payment_type === 110 ||
      cart?.payment_type === 112 ||
      cart?.payment_type === 114 ||
      cart?.payment_type === 118) &&
    cart?.status >= 1 &&
    cart?.payment_process?.qrcode;

  const getPaymentMethodLabel = paymentType => {
    if (paymentType === 119) return 'Google Pay';
    if (paymentType === 120) return 'Apple Pay';
    return 'Cartão';
  };

  const isBnplPayment = [115, 116].includes(cart?.payment_type);

  const { isAdmin, isStoreManager, isSupport } = useRoles();

  const showReprocessBtn = useMemo(() => {
    if (
      cart?.status === 0 &&
      cart.status_text === 'Pagamento rejeitado pelo anti-fraude' &&
      cart.store.payment_gateway === 'cielo' &&
      (isAdmin || isStoreManager)
    ) {
      return true;
    }
    return false;
  }, [cart, isAdmin, isStoreManager]);

  const handleReprocessor = useCallback(async () => {
    try {
      await api.patch(`${role}/carts/${cart.id}/payment/force`);
      history.push('/');
      toast.success('Pagamento reprocessado com sucesso!');
    } catch (err) {
      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      }
    }
  }, [cart, role]);

  const handleChangeback = useCallback(async () => {
    try {
      await api.patch(`${role}/carts/${cart.id}/payment/cancel`);
      history.push('/');
      toast.success('Pagamento estornado com sucesso!');
    } catch (err) {
      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      }
    }
  }, [cart, role]);

  if (cart?.is_suitcase_delivery && cart?.status <= 8) return null;

  if (
    !cart?.is_for_delivery &&
    cart?.store?.hide_payment_data &&
    cart?.confirmation_code &&
    cart?.status_text === 'Encaminhado por estoquista'
  )
    return null;

  if (
    (!cart?.is_suitcase_delivery &&
      (cart?.status === 1 ||
        (cart?.payment_type !== 201 &&
          cart?.payment_type !== 202 &&
          cart?.payment_type !== 203 &&
          cart?.payment_type !== 204 &&
          cart?.payment_type !== 401 &&
          !['Paid', 'canceled'].includes(cart.payment_process?.status) &&
          !cart?.payment_process?.transaction_id))) ||
    (cart?.payment_process?.status === 'failed' &&
      !cart.status_text === 'Pagamento rejeitado pelo anti-fraude')
  )
    return null;

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <Row
          className="w-100 mx-0 cursor-pointer"
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          <Col className="font-size-16 font-weight-600 px-0">
            <Row className="w-100 mx-0 justify-content-between">
              <div className="px-0 mb-0">Pagamento</div>
              <div className="px-0 mb-0">
                <IconButton
                  icon={`las la-${expanded ? 'angle-up' : 'angle-right'}`}
                  click={() => {
                    setExpanded(!expanded);
                  }}
                  color="gray-700"
                />
              </div>
            </Row>
          </Col>
        </Row>
        <Collapse isOpen={expanded}>
          {cart?.payment_type === 401 && (
            <Row className="mx-0 mt-3">
              <Col lg={12} className="px-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Método
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  Pago antecipadamente
                </div>
              </Col>
            </Row>
          )}
          {cart?.payment_type === 201 && (
            <Row className="mx-0 mt-3">
              <Col lg={6} className="px-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Método
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  Maquineta (Crédito)
                </div>
              </Col>
              <Col lg={6} className="w-100 px-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Parcelas
                </div>
                {cart?.payment_process?.installments ? (
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {cart?.payment_process?.installments === 1
                      ? 'Pagamento à vista'
                      : `Parcelado em ${cart?.payment_process?.installments}x`}
                  </div>
                ) : (
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    -
                  </div>
                )}
              </Col>
            </Row>
          )}
          {cart?.payment_type === 202 && (
            <Row className="mx-0 mt-3">
              <Col lg={6} className="px-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Método
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  Maquineta (Débito)
                </div>
              </Col>
              <Col lg={6} className="w-100 px-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Parcelas
                </div>
                {cart?.payment_process?.installments ? (
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {cart?.payment_process?.installments === 1
                      ? 'Pagamento à vista'
                      : `Parcelado em ${cart?.payment_process?.installments}x`}
                  </div>
                ) : (
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    -
                  </div>
                )}
              </Col>
            </Row>
          )}
          {cart?.payment_type === 203 && (
            <Row className="mx-0 mt-3">
              <Col lg={6} className="px-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Método
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  Dinheiro
                </div>
              </Col>
              <Col xs={12} lg={6} className="w-100 px-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Parcelas
                </div>
                {cart?.payment_process?.installments ? (
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {cart?.payment_process?.installments === 1
                      ? 'Pagamento à vista'
                      : `Parcelado em ${cart?.payment_process?.installments}x`}
                  </div>
                ) : (
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    -
                  </div>
                )}
              </Col>
              <Col xs={12} lg={6} className="w-100 px-0 mt-3">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Troco para
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  {Number(cart?.payment_process?.change)
                    ? formatPrice(cart?.payment_process?.change)
                    : '-'}
                </div>
              </Col>
            </Row>
          )}
          {cart?.payment_type === 204 && (
            <Row className="mx-0 mt-3">
              <Col lg={6} className="px-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Método
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  Maquineta (Pix)
                </div>
              </Col>
            </Row>
          )}
          {isBnplPayment && (
            <Row className="mx-0 mt-3">
              <Col lg={6} className="px-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Método
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  Pix Parcelado
                </div>
              </Col>
              <Col lg={6} className="px-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Identificador da transação
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  {cart.payment_process?.gateway_id}
                </div>
              </Col>
              <Col lg={6} className="px-0 mb-2 mb-lg-0 mt-3">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Status
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  {cart.payment_process?.status === 'Pending' &&
                    cart.payment_process?.reason === 'FirstPayment' &&
                    'Primeiro Pagamento Pendente'}

                  {cart.payment_process?.status === 'Pending' &&
                    cart.payment_process?.reason === 'EmailValidation' &&
                    'Validação Pendente'}

                  {cart.payment_process?.status === 'Pending' &&
                    cart.payment_process?.reason === 'ProviderReview' &&
                    'Aprovação do Fornecedor Pendente'}

                  {cart.payment_process?.status === 'Paid' && 'Pago'}
                </div>
              </Col>
              <Col xs={12} lg={6} className="w-100 px-0 mt-3">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Parcelas
                </div>
                {cart?.payment_process?.installment_option?.installments ? (
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {cart?.payment_process?.installment_option?.installments ===
                    1
                      ? 'Pagamento à vista'
                      : `Parcelado em ${cart?.payment_process?.installment_option?.installments}x`}
                  </div>
                ) : (
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    -
                  </div>
                )}
              </Col>
            </Row>
          )}
          {isLinkPayment && (
            <Row className="mx-0 mt-3">
              <Col lg={6} className="px-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Método
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  Pix
                </div>
              </Col>
              <Col lg={6} className="px-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Identificador da transação
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  {cart.payment_process?.gateway_id}
                </div>
              </Col>
            </Row>
          )}
          {(cart?.payment_process?.payment_method === 'credit_card' ||
            (cart?.payment_process?.payment_method !== 'bnpl' &&
              cart?.payment_process?.transaction_id &&
              !cart?.payment_process?.qrcode)) && (
            <>
              <Row className="w-100 mx-0 mt-3 mb-lg-4">
                <Col lg={6} className="px-0 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Método
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {getPaymentMethodLabel(cart?.payment_type)}
                  </div>
                </Col>
                <Col lg={6} className="px-0 pl-lg-3 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Código de autorização
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {cart.payment_process?.authorization_code || '-'}
                  </div>
                </Col>
              </Row>
              <Row className="w-100 mx-0 mb-lg-4">
                <Col lg={6} className="pl-0 pr-0 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    NSU
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {cart?.payment_process?.nsu || '-'}
                  </div>
                </Col>
                <Col lg={6} className="px-0 pl-lg-3 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Nome
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {isSupport
                      ? cart?.payment_process?.card_holder_name || '-'
                      : maskName(cart?.payment_process?.card_holder_name)}
                  </div>
                </Col>
              </Row>
              {isSupport && (
                <Row className="w-100 mx-0 mb-lg-4">
                  <Col lg={6} className="pl-0 pr-0 mb-2 mb-lg-0">
                    <div className="font-size-14 font-weight-600 text-gray-600">
                      Últimos digitos
                    </div>
                    <div className="font-size-14 font-weight-400 text-gray-700">
                      {cart?.payment_process?.card_last_digits || '-'}
                    </div>
                  </Col>
                  <Col lg={6} className="px-0 pl-lg-3 mb-2 mb-lg-0">
                    <div className="font-size-14 font-weight-600 text-gray-600">
                      Bandeira
                    </div>
                    <div className="font-size-14 font-weight-400 text-gray-700">
                      {cart?.payment_process?.card_brand || '-'}
                    </div>
                  </Col>
                </Row>
              )}
              {!isSupport && (
                <Row className="w-100 mx-0 mb-lg-4">
                  <Col lg={6} className="px-0 mb-2 mb-lg-0">
                    <div className="font-size-14 font-weight-600 text-gray-600">
                      Bandeira
                    </div>
                    <div className="font-size-14 font-weight-400 text-gray-700">
                      {cart?.payment_process?.card_brand || '-'}
                    </div>
                  </Col>
                  <Col lg={6} className="pl-0 pl-lg-3 mb-0 mb-lg-0">
                    <div className="font-size-14 font-weight-600 text-gray-600">
                      Parcelas
                    </div>
                    {cart?.payment_process?.installments ? (
                      <div className="font-size-14 font-weight-400 text-gray-700">
                        {cart?.payment_process?.installments === 1
                          ? 'Pagamento à vista'
                          : `Parcelado em ${cart?.payment_process?.installments}x`}
                      </div>
                    ) : (
                      <div className="font-size-14 font-weight-400 text-gray-700">
                        -
                      </div>
                    )}
                  </Col>
                </Row>
              )}
              {isSupport && (
                <Row className="w-100 mx-0 mb-lg-4">
                  <Col lg={12} className="pl-0 pr-0 mb-0 mb-lg-0">
                    <div className="font-size-14 font-weight-600 text-gray-600">
                      Parcelas
                    </div>
                    {cart?.payment_process?.installments ? (
                      <div className="font-size-14 font-weight-400 text-gray-700">
                        {cart?.payment_process?.installments === 1
                          ? 'Pagamento à vista'
                          : `Parcelado em ${cart?.payment_process?.installments}x`}
                      </div>
                    ) : (
                      <div className="font-size-14 font-weight-400 text-gray-700">
                        -
                      </div>
                    )}
                  </Col>
                </Row>
              )}
            </>
          )}
          {showReprocessBtn && (
            <div className="pt-3">
              <Button
                // color="primary"
                className="new-layout-btn main mr-lg-2 mb-2 mb-lg-0"
                onClick={handleReprocessor}
                outline
              >
                <i className="fa fa-spinner pl-1 mr-2 font-size-14" />
                Aprovar
              </Button>

              <Button
                // color="primary"
                className="new-layout-btn main ml-lg-2 mb-2 mb-lg-0"
                onClick={handleChangeback}
                outline
              >
                <i className="fas fa-undo pl-1 mr-2 font-size-14" />
                Estornar
              </Button>
            </div>
          )}
        </Collapse>
      </CardBody>
    </Card>
  );
};

export const BillingInfoCard = ({ cart }) => {
  const [expanded, setExpanded] = useState(window.innerWidth > 768);
  const { isSupport } = useRoles();
  const [revealBilling, setRevealBilling] = useState(false);
  const canRevealBilling = Boolean(isSupport);
  const showFullBilling = canRevealBilling && revealBilling;

  const charge = cart?.payment_process?.charge;

  let birthDateText = '-';
  const birthDateRaw = charge?.birth_date;

  if (birthDateRaw) {
    if (showFullBilling) {
      try {
        birthDateText = format(parseISO(birthDateRaw), 'dd/MM/yyyy');
      } catch (e) {
        birthDateText = '-';
      }
    } else {
      birthDateText = maskBirthDate(birthDateRaw);
    }
  }

  const billingAddressValue = (() => {
    const a = charge?.address;
    if (!a) return '-';
    if (!showFullBilling) return '****';

    const street = a.street_name || '';
    const number = a.number || '';
    const neighborhood = a.neighborhood || '';
    const city = a.city || '';
    const state = a.state || '';
    const cep = a.cep || '';

    return `${street}, ${number} - ${neighborhood}, ${city} - ${state}, CEP ${cep}`.trim();
  })();

  if (cart?.is_suitcase_delivery && cart?.status <= 8) return null;

  if (
    !cart?.is_for_delivery &&
    cart?.store?.hide_payment_data &&
    cart?.confirmation_code &&
    cart?.status_text === 'Encaminhado por estoquista'
  )
    return null;

  if (
    !cart?.is_suitcase_delivery &&
    (cart?.status === 1 ||
      cart?.payment_process?.payment_method !== 'credit_card' ||
      (cart?.payment_process?.payment_method === 'bnpl' &&
        cart?.payment_process?.qrcode) ||
      (cart?.payment_type !== 201 &&
        cart?.payment_type !== 202 &&
        cart?.payment_type !== 203 &&
        cart?.payment_type !== 204 &&
        cart?.payment_type !== 401 &&
        !['Paid', 'canceled'].includes(cart.payment_process?.status) &&
        !cart?.payment_process?.transaction_id))
  )
    return null;

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <Row className="w-100 mx-0">
          <Col className="w-100 px-0">
            <div
              className="w-100 font-size-16 font-weight-600 d-flex align-items-center cursor-pointer"
              onClick={() => setExpanded(v => !v)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') setExpanded(v => !v);
              }}
            >
              <span className="ml-0">Dados de Cobrança</span>
              <div className="ml-auto d-flex align-items-center">
                {canRevealBilling && (
                  <button
                    type="button"
                    className="p-0 border-0 bg-transparent d-flex align-items-center text-gray-600 reveal-btn"
                    onClick={e => {
                      e.stopPropagation();

                      setRevealBilling(prev => {
                        const next = !prev;

                        if (next) {
                          logPiiRevealOnce({
                            referId: cart?.id,
                            referModel: 'App/Cart',
                            fields: [
                              'cpf',
                              'email',
                              'telephone',
                              'birth_date',
                              'billing_address',
                            ],
                            scope: 'billing_card',
                          });
                        }

                        return next;
                      });
                    }}
                    aria-pressed={revealBilling}
                  >
                    <i
                      className={`las la-${
                        revealBilling ? 'eye-slash' : 'eye'
                      } font-size-18`}
                    />
                    <span className="ml-1 font-size-12">
                      {revealBilling ? 'Ocultar dados' : 'Revelar dados'}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-link p-0 ml-3 text-gray-600"
                  onClick={e => {
                    e.stopPropagation();
                    setExpanded(v => !v);
                  }}
                  aria-label={expanded ? 'Recolher' : 'Expandir'}
                >
                  <i
                    className={`las la-${
                      expanded ? 'angle-up' : 'angle-right'
                    } font-size-20`}
                  />
                </button>
              </div>
            </div>
          </Col>
        </Row>
        <Collapse isOpen={expanded}>
          {(cart?.payment_process?.payment_method === 'credit_card' ||
            (cart?.payment_process?.payment_method !== 'bnpl' &&
              !cart?.payment_process?.qrcode)) && (
            <>
              <Row className="w-100 mx-0 mt-3 mb-lg-4">
                <Col lg={12} className="px-0 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Nome
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {cart.payment_process?.charge?.name || '-'}
                  </div>
                </Col>
              </Row>
              <Row className="w-100 mx-0 mb-lg-4">
                <Col lg={6} className="pl-0 pr-0 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    E-mail
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {showFullBilling
                      ? cart.payment_process?.charge?.email || '-'
                      : maskEmail(cart.payment_process?.charge?.email)}
                  </div>
                </Col>
                <Col lg={6} className="pl-0 pr-0 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Telefone
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {showFullBilling
                      ? cart.payment_process?.charge?.telephone || '-'
                      : maskPhone(cart.payment_process?.charge?.telephone)}
                  </div>
                </Col>
              </Row>
              <Row className="w-100 mx-0 mb-lg-4">
                <Col lg={6} className="pl-0 pr-0 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    CPF
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {showFullBilling
                      ? cart.payment_process?.charge?.cpf || '-'
                      : maskCPF(cart.payment_process?.charge?.cpf)}
                  </div>
                </Col>
                <Col lg={6} className="pl-0 pr-0 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Data de Nascimento
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {birthDateText}
                  </div>
                </Col>
              </Row>
              <Row className="w-100 mx-0 mb-lg-4">
                <Col lg={12} className="pl-0 pr-0 mb-0 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Endereço de Cobrança
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {billingAddressValue}
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Collapse>
      </CardBody>
    </Card>
  );
};

export const ColectInfoCard = ({ cart }) => {
  const [expanded, setExpanded] = useState(window.innerWidth > 768);

  const deliveryFleet = useMemo(
    () => FLEETS.find(fleet => fleet.tag === cart?.return_delivery_method),
    [cart]
  );

  if (
    (cart.return_delivery?.status !== DELIVERED && cart?.status < 8) ||
    cart?.status === 13 ||
    cart?.status === 14 ||
    !cart?.return_delivery_address
  )
    return null;

  if (!cart.is_for_delivery) return null;

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <Row
          className="w-100 mx-0 cursor-pointer"
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          <Col className="font-size-16 font-weight-600 px-0">
            <Row className="w-100 mx-0 justify-content-between">
              <div className="px-0 mb-0">
                {cart?.is_suitcase_delivery ? 'Coleta' : 'Devolução'}
              </div>
              <div className="px-0 mb-0">
                <IconButton
                  icon={`las la-${expanded ? 'angle-up' : 'angle-right'}`}
                  click={() => {
                    setExpanded(!expanded);
                  }}
                  color="gray-700"
                />
              </div>
            </Row>
          </Col>
        </Row>
        <Collapse isOpen={expanded}>
          <Row className="w-100 mx-0 mt-3 mb-lg-4">
            <Col lg={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Endereço de{' '}
                {cart?.is_suitcase_delivery ? 'coleta' : 'devolução'}
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {cart?.return_delivery_address?.street_name},{' '}
                {cart?.return_delivery_address?.number} -{' '}
                {cart?.return_delivery_address?.neighborhood},{' '}
                {cart?.return_delivery_address?.city} -{' '}
                {cart?.return_delivery_address?.state}, CEP{' '}
                {cart?.return_delivery_address?.cep}
              </div>
            </Col>
            <Col lg={6} className="pl-0 pl-lg-3 pr-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Endereço de entrega
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {cart?.return_store?.fantasy_name}
              </div>
            </Col>
          </Row>
          <Row className="w-100 mx-0 mb-lg-4">
            <Col xs={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Transportadora
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {deliveryFleet?.tag !== 'PROPRIA'
                  ? deliveryFleet?.label
                  : 'Alternativa'}
              </div>
            </Col>
            <Col xs={6} className="px-0 pl-lg-3 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Veículo
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {getDeliveryVehicleLabel(cart.return_delivery_vehicle)}
              </div>
            </Col>
          </Row>
          <Row className="w-100 mx-0 mb-lg-4">
            <Col xs={6} className="pl-0 pr-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Situação da entrega
              </div>
              {cart.return_delivery?.status_text ? (
                <span
                  className={`font-size-14 font-weight-400 ${defineDeliveryStatusColor(
                    cart.return_delivery?.status_text
                  )}`}
                >
                  {cart.return_delivery?.status_text}
                </span>
              ) : (
                <span className="font-size-14 font-weight-400 text-gray-700">
                  -
                </span>
              )}
            </Col>
            <Col xs={6} className="pl-0 pl-lg-3 pr-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Nome do entregador
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {cart.return_delivery?.worker_name ?? '-'}
              </div>
            </Col>
          </Row>
          <Row className="w-100 mx-0 mb-lg-4">
            <Col xs={6} className="pl-0 pr-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Tel do entregador
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {cart.return_delivery?.worker_phone
                  ? formatPhone(cart.return_delivery?.worker_phone)
                  : '-'}
              </div>
            </Col>
            {cart?.return_delivery_method === 'UBER' ? (
              <Col xs={6} className="pl-0 pl-lg-3 pr-0 mb-2 mb-lg-4">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Uber ID
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  {cart.return_delivery?.uber_uuid || '-'}
                </div>
              </Col>
            ) : (
              <Col xs={6} className="pl-0 pl-lg-3 pr-0 mb-2 mb-lg-4">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  ID da entrega
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  {cart.return_delivery?.id_on_service || '-'}
                </div>
              </Col>
            )}
            {cart?.return_delivery?.finalization_reason && (
              <>
                <Col xs={6} className="px-0 mb-2 mb-lg-0 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Motivo de finalização
                  </div>
                  <span className="font-size-14 font-weight-400">
                    {cart?.return_delivery?.finalization_reason}
                  </span>
                </Col>
              </>
            )}
            {cart?.return_delivery?.finalization_details && (
              <>
                <Col xs={6} className="pl-0 pl-lg-3 pr-0 mb-2 mb-lg-0 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Detalhes da finalização
                  </div>
                  <span className="font-size-14 font-weight-400">
                    {cart?.return_delivery?.finalization_details}
                  </span>
                </Col>
              </>
            )}
          </Row>
          <Row className="w-100 mx-0 mb-lg-4">
            {cart?.status >= 8 && (
              <Col xs={12} lg={6} className="pl-0 pr-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Avaliação de entrega
                </div>
                {cart?.return_delivery_score ? (
                  <div className="font-size-14 font-weight-400 text-gray-700 mt-1">
                    {cart?.return_delivery_score.score >= 1 ? (
                      <FaStar color="#ffd600" size={30} />
                    ) : (
                      <FaRegStar color="#2544b1" size={30} />
                    )}
                    {cart?.return_delivery_score.score >= 2 ? (
                      <FaStar color="#ffd600" size={30} />
                    ) : (
                      <FaRegStar color="#2544b1" size={30} />
                    )}
                    {cart?.return_delivery_score.score >= 3 ? (
                      <FaStar color="#ffd600" size={30} />
                    ) : (
                      <FaRegStar color="#2544b1" size={30} />
                    )}
                    {cart?.return_delivery_score.score >= 4 ? (
                      <FaStar color="#ffd600" size={30} />
                    ) : (
                      <FaRegStar color="#2544b1" size={30} />
                    )}
                    {cart?.return_delivery_score.score >= 5 ? (
                      <FaStar color="#ffd600" size={30} />
                    ) : (
                      <FaRegStar color="#2544b1" size={30} />
                    )}
                  </div>
                ) : (
                  'Sem avaliação'
                )}
              </Col>
            )}
          </Row>
        </Collapse>
      </CardBody>
    </Card>
  );
};

export const DeliveryInfoCard = ({ cart }) => {
  const [expanded, setExpanded] = useState(window.innerWidth > 768);

  const deliveryFleet = useMemo(
    () => FLEETS.find(fleet => fleet.tag === cart?.delivery_method),
    [cart]
  );

  if (
    cart.delivery &&
    cart.delivery?.status !== DELIVERED &&
    cart?.status < 7 &&
    cart?.status !== 6 &&
    cart?.status !== 4
  )
    return null;

  if (cart.delivery_method === 'CORREIOS') return null;

  if (!cart.is_for_delivery)
    return (
      <Card className="new-layout card">
        <CardBody className="new-layout card-body px-3 py-3">
          <Row
            className="w-100 mx-0 cursor-pointer"
            onClick={() => {
              setExpanded(!expanded);
            }}
          >
            <Col className="font-size-16 font-weight-600 px-0">
              <Row className="w-100 mx-0 d-flex justify-content-between">
                <div className="px-0 mb-0">Entrega</div>
                <div className="px-0 mb-0">
                  <IconButton
                    icon={`las la-${expanded ? 'angle-up' : 'angle-right'}`}
                    click={() => {
                      setExpanded(!expanded);
                    }}
                    color="gray-700"
                  />
                </div>
              </Row>
            </Col>
          </Row>
          <Collapse isOpen={expanded}>
            <Row className="w-100 mx-0 mt-3 mb-0">
              <Col lg={12} className="px-0 mb-lg-0 pb-3">
                <div className="font-size-14 font-weight-600 text-gray-800">
                  Retirada em loja
                </div>
              </Col>
              <Col lg={12} className="px-0 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Loja de retirada
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  {cart.store.fantasy_name}
                </div>
              </Col>
            </Row>
          </Collapse>
        </CardBody>
      </Card>
    );

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <Row
          className="w-100 mx-0 cursor-pointer"
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          <Col className="font-size-16 font-weight-600 px-0">
            <Row className="w-100 mx-0 d-flex justify-content-between">
              <div className="px-0 mb-0">Entrega</div>
              <div className="px-0 mb-0">
                <IconButton
                  icon={`las la-${expanded ? 'angle-up' : 'angle-right'}`}
                  click={() => {
                    setExpanded(!expanded);
                  }}
                  color="gray-700"
                />
              </div>
            </Row>
          </Col>
        </Row>
        <Collapse isOpen={expanded}>
          <Row className="w-100 mx-0 mt-3 mb-lg-4">
            <Col lg={6} className="px-0 mb-2 mb-lg-0 mb-lg-4">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Endereço de coleta
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {cart.store.fantasy_name}
              </div>
            </Col>
            <Col lg={6} className="pl-0 pl-lg-3 pr-0 mb-2 mb-lg-0 mb-lg-4">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Endereço de entrega
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {cart.delivery_address?.full_address || '-'}
              </div>
            </Col>
            <Col xs={6} className="px-0 mb-2 mb-lg-0 mb-lg-4">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Transportadora
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {deliveryFleet?.tag !== 'PROPRIA'
                  ? deliveryFleet?.label
                  : 'Alternativa'}
              </div>
            </Col>
            <Col xs={6} className="px-0 mb-2 mb-lg-0 pl-lg-3">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Veículo
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {getDeliveryVehicleLabel(cart.delivery_vehicle)}
              </div>
            </Col>
            <Col xs={6} className="pl-0 pr-0 mb-2 mb-lg-0 mb-lg-4">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Situação da entrega
              </div>
              {cart.delivery?.status_text ? (
                <span
                  className={`font-size-14 font-weight-400 ${defineDeliveryStatusColor(
                    cart.delivery?.status_text
                  )}`}
                >
                  {cart.delivery?.status_text}
                </span>
              ) : (
                <span className="font-size-14 font-weight-400 text-gray-700">
                  {cart?.delivery ? '-' : 'Aguardando envio'}
                </span>
              )}
            </Col>
            {cart.delivery && (
              <>
                <Col xs={6} className="pl-0 pl-lg-3 pr-0 mb-2 mb-lg-4">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Nome do entregador
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {cart.delivery?.worker_name ?? '-'}
                  </div>
                </Col>
                <Col xs={6} className="pl-0 pr-0 mb-2 mb-lg-4">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Tel do entregador
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {cart.delivery?.worker_phone
                      ? formatPhone(cart.delivery?.worker_phone)
                      : '-'}
                  </div>
                </Col>
              </>
            )}
            {cart?.delivery_method === 'UBER' ? (
              <Col xs={6} className="pl-0 pl-lg-3 pr-0 mb-2 mb-lg-4">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Uber ID
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  {cart.delivery?.uber_uuid || '-'}
                </div>
              </Col>
            ) : (
              <Col xs={6} className="pl-0 pl-lg-3 pr-0 mb-2 mb-lg-4">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  ID da entrega
                </div>
                <div className="font-size-14 font-weight-400 text-gray-700">
                  {cart.delivery?.id_on_service || '-'}
                </div>
              </Col>
            )}
            {cart?.delivery?.finalization_reason && (
              <>
                <Col xs={6} className="px-0 mb-2 mb-lg-0 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Motivo de finalização
                  </div>
                  <span className="font-size-14 font-weight-400">
                    {cart?.delivery?.finalization_reason}
                  </span>
                </Col>
              </>
            )}
            {cart?.delivery?.finalization_details && (
              <>
                <Col xs={6} className="pl-0 pl-lg-3 pr-0 mb-2 mb-lg-0 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    Detalhes da finalização
                  </div>
                  <span className="font-size-14 font-weight-400">
                    {cart?.delivery?.finalization_details}
                  </span>
                </Col>
              </>
            )}
          </Row>
          <Row className="w-100 mx-0 mb-lg-0">
            {(cart?.status >= 7 || cart?.status === 4) && (
              <Col xs={12} lg={6} className="pl-0 pr-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Avaliação de entrega
                </div>
                {cart?.delivery_score ? (
                  <div className="font-size-14 font-weight-400 text-gray-700 mt-1">
                    {cart?.delivery_score.score >= 1 ? (
                      <FaStar color="#ffd600" size={30} />
                    ) : (
                      <FaRegStar color="#2544b1" size={30} />
                    )}
                    {cart?.delivery_score.score >= 2 ? (
                      <FaStar color="#ffd600" size={30} />
                    ) : (
                      <FaRegStar color="#2544b1" size={30} />
                    )}
                    {cart?.delivery_score.score >= 3 ? (
                      <FaStar color="#ffd600" size={30} />
                    ) : (
                      <FaRegStar color="#2544b1" size={30} />
                    )}
                    {cart?.delivery_score.score >= 4 ? (
                      <FaStar color="#ffd600" size={30} />
                    ) : (
                      <FaRegStar color="#2544b1" size={30} />
                    )}
                    {cart?.delivery_score.score >= 5 ? (
                      <FaStar color="#ffd600" size={30} />
                    ) : (
                      <FaRegStar color="#2544b1" size={30} />
                    )}
                  </div>
                ) : (
                  'Sem avaliação'
                )}
              </Col>
            )}
          </Row>
          <Row className="w-100 mx-0 mb-lg-0">
            {cart?.delivery_method === 'UBER' &&
              cart?.status === 10 &&
              [CANCELLED, RETURNED].includes(cart?.delivery?.status) &&
              (cart?.delivery?.qr_code || cart?.return_delivery?.qr_code) && (
                <Col xs={12} lg={12} className="pl-0 mt-3 ml-lg-1 pr-lg-2">
                  <DeliveryQrcode
                    qrCode={
                      cart?.delivery?.qr_code || cart?.return_delivery?.qr_code
                    }
                    sale={cart}
                  />
                </Col>
              )}
          </Row>
        </Collapse>
      </CardBody>
    </Card>
  );
};

export const CorreiosInfoCard = ({ cart }) => {
  const [expanded, setExpanded] = useState(
    typeof window !== 'undefined' ? window.innerWidth > 768 : true
  );
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);

  const parsed = useMemo(() => {
    const base = {
      service: {
        type: null, // "PAC" | "SEDEX"
        price: null,
        deadline_days: null,
        estimated_delivery_date: null,
        service_code: null,
        package: null,
      },
      tracking: {
        code: null,
        currentStatus: null,
        events: [],
      },
    };

    const normalizePackage = pkg => {
      if (!pkg) return null;

      // formato do estimate detalhado
      if (
        pkg.peso !== undefined ||
        pkg.comprimento !== undefined ||
        pkg.largura !== undefined ||
        pkg.altura !== undefined
      ) {
        return {
          box_name: pkg.box_name || null,
          weight: pkg.peso ?? null,
          length: pkg.comprimento ?? null,
          width: pkg.largura ?? null,
          height: pkg.altura ?? null,
          diametro: pkg.diametro ?? null,
          formato: pkg.formato ?? null,
        };
      }

      // formato do selected (width/length/height/weight)
      return {
        box_name: pkg.box_name || null,
        weight: pkg.weight ?? null,
        length: pkg.length ?? null,
        width: pkg.width ?? null,
        height: pkg.height ?? null,
        diametro: pkg.diameter ?? null,
        formato: pkg.format ?? null,
      };
    };

    // ---------------------------
    // 1) Fonte principal: delivery_estimates
    // ---------------------------
    let estimates = cart?.delivery_estimates;

    if (typeof estimates === 'string') {
      try {
        estimates = JSON.parse(estimates);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erro ao fazer parse de delivery_estimates:', e);
        estimates = null;
      }
    }

    const correiosEst = estimates?.CORREIOS || null;
    const selected = correiosEst?.selected || null;
    const selectedType = selected?.type || null; // "PAC" | "SEDEX"
    const selectedDetails = selectedType ? correiosEst?.[selectedType] : null;

    const deadlineDays =
      selectedDetails?.deadline ??
      (() => {
        const text = selected?.deadline; // ex "5 dias úteis"
        if (!text) return null;
        const m = String(text).match(/\d+/);
        return m ? Number(m[0]) : null;
      })();

    const pkgRaw = selectedDetails?.package || selected?.package || null;

    const serviceFromEstimates = {
      type: selectedType,
      price: selectedDetails?.price ?? selected?.price ?? null,
      deadline_days: deadlineDays ?? null,
      estimated_delivery_date: selectedDetails?.estimated_delivery_date ?? null,
      service_code: selectedDetails?.service_code ?? null,
      package: normalizePackage(pkgRaw),
    };

    // ---------------------------
    // 2) Fallback: delivery.metadata (service + tracking)
    // ---------------------------
    let metadata = cart?.delivery?.metadata;

    if (metadata && typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erro ao fazer parse de delivery.metadata:', e);
        metadata = null;
      }
    }

    const isCorreiosMetadata = !!metadata && metadata?.courier === 'CORREIOS';
    const serviceFromMetadata = isCorreiosMetadata
      ? metadata?.service || {}
      : {};
    const trackingFromMetadata = isCorreiosMetadata
      ? metadata?.tracking || {}
      : {};

    const events = Array.isArray(trackingFromMetadata?.events)
      ? trackingFromMetadata.events
      : [];

    // Merge final (prefer estimates, completa com metadata)
    return {
      service: {
        ...base.service,
        type: serviceFromEstimates.type || serviceFromMetadata.type || null,
        price: serviceFromEstimates.price ?? serviceFromMetadata.price ?? null,
        deadline_days:
          serviceFromEstimates.deadline_days ??
          Number(serviceFromMetadata.deadline_days) ??
          null,
        estimated_delivery_date:
          serviceFromEstimates.estimated_delivery_date ||
          serviceFromMetadata.estimated_delivery_date ||
          null,
        service_code:
          serviceFromEstimates.service_code ||
          serviceFromMetadata.service_code ||
          null,
        package:
          serviceFromEstimates.package ||
          (serviceFromMetadata.package
            ? normalizePackage(serviceFromMetadata.package)
            : null),
      },
      tracking: {
        ...base.tracking,
        code: trackingFromMetadata?.code || null,
        currentStatus:
          trackingFromMetadata?.current_status || 'Aguardando envio',
        events,
      },
    };
  }, [cart?.delivery_estimates, cart?.delivery?.metadata]);

  const alwaysShowCorreios = cart?.status === 2 || cart?.status === 3;

  // ✅ regras de exibição (depois do hook)
  if (
    !alwaysShowCorreios &&
    cart?.delivery &&
    cart?.delivery?.status !== DELIVERED &&
    cart?.status < 7 &&
    cart?.status !== 6 &&
    cart?.status !== 4
  ) {
    return null;
  }

  if (cart?.delivery_method !== 'CORREIOS') return null;

  const { service, tracking } = parsed;

  // helpers UI
  const has = v => v !== null && v !== undefined && String(v).trim() !== '';

  function formatCurrencyBRL(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '-';
    }
    try {
      return Number(value).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      });
    } catch {
      return `R$ ${value}`;
    }
  }

  function formatDate(value) {
    if (!value) return '-';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-');
      return `${day}/${month}/${year}`;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  function formatDateTime(value) {
    if (!value) return '-';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    const horas = String(date.getHours()).padStart(2, '0');
    const minutos = String(date.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  const prazoLabel = Number.isFinite(Number(service.deadline_days))
    ? `${Number(service.deadline_days)} dia${
        Number(service.deadline_days) === 1 ? '' : 's'
      } ${Number(service.deadline_days) === 1 ? 'útil' : 'úteis'}`
    : '-';

  const hasEvents =
    Array.isArray(tracking?.events) && tracking.events.length > 0;

  const tooltipId = `package-info-${
    cart?.id || service?.service_code || 'correios'
  }`;

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <Row
          className="w-100 mx-0 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <Col className="font-size-16 font-weight-600 px-0">
            <Row className="w-100 mx-0 d-flex justify-content-between align-items-center">
              <span className="px-0">Entrega</span>
              <IconButton
                icon={`las la-${expanded ? 'angle-up' : 'angle-right'}`}
                click={() => setExpanded(!expanded)}
                color="gray-700"
              />
            </Row>
          </Col>
        </Row>

        <Collapse isOpen={expanded}>
          <Row className="w-100 mx-0 pt-3">
            <Col md={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Endereço de coleta
              </div>
              <div className="text-gray-700 font-size-13">
                {cart.store?.fantasy_name || '-'}
              </div>
            </Col>

            <Col md={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Endereço de entrega
              </div>
              <div className="text-gray-700 font-size-13">
                {cart.delivery_address?.full_address || '-'}
              </div>
            </Col>
          </Row>

          <Row className="w-100 mx-0 pt-lg-3">
            <Col md={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Transportadora
              </div>
              <div className="font-size-14 text-gray-700">Correios</div>
            </Col>

            <Col md={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Método
              </div>
              <div className="font-size-14 text-gray-700">
                {service.type || '-'}
              </div>
            </Col>
          </Row>

          <Row className="w-100 mx-0 pt-lg-3">
            <Col md={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Valor
              </div>
              <div className="font-size-14 text-gray-700">
                {formatCurrencyBRL(service.price)}
              </div>
            </Col>

            <Col md={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Pacote
              </div>

              {service?.package ? (
                <div className="font-size-14 text-gray-700 d-flex align-items-center">
                  <span>{service.package.box_name || 'Não identificado!'}</span>

                  {has(service?.service_code) && (
                    <>
                      <i
                        id={tooltipId}
                        className="las la-info-circle font-size-16 text-secondary ml-1 cursor-pointer"
                      />
                      <UncontrolledTooltip placement="top" target={tooltipId}>
                        Largura: {service.package.width ?? '-'} cm <br />
                        Comprimento: {service.package.length ?? '-'} cm <br />
                        Altura: {service.package.height ?? '-'} cm <br />
                        Peso: {service.package.weight ?? '-'} kg
                      </UncontrolledTooltip>
                    </>
                  )}
                </div>
              ) : (
                <div className="font-size-14 text-gray-700">-</div>
              )}
            </Col>
          </Row>

          <Row className="w-100 mx-0 pt-lg-3">
            <Col md={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Prazo
              </div>
              <div className="font-size-14 text-gray-700">{prazoLabel}</div>
            </Col>

            <Col md={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Previsão de entrega
              </div>
              <div className="font-size-14 text-gray-700">
                {formatDate(service.estimated_delivery_date)}
              </div>
            </Col>
          </Row>

          <hr />

          <Row className="w-100 mx-0 mt-2">
            <Col md={6} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Código de rastreio
              </div>
              <div className="font-size-14 text-gray-700">
                {tracking.code || '-'}
              </div>
            </Col>

            <Col md={6} className="px-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Status
              </div>
              <div className="font-size-14 text-gray-700">
                {tracking.currentStatus || '-'}
              </div>
            </Col>
          </Row>

          {!hasEvents && (
            <Row className="w-100 mx-0 pt-3">
              <Col className="px-0">
                <div className="text-muted font-size-13">
                  O código de rastreio e o histórico de eventos aparecem após a
                  criação da pré-postagem.
                </div>
              </Col>
            </Row>
          )}

          {(hasEvents || hasCorreiosLabel(cart)) && (
            <Row className="w-100 mx-0 pt-3 align-items-center">
              <Col className="px-0">
                <CorreiosLabelDownloadButton cart={cart} />
              </Col>
              {hasEvents && (
                <Col xs="auto" className="px-0 pl-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => setIsTimelineOpen(true)}
                  >
                    Ver histórico
                  </button>
                </Col>
              )}
            </Row>
          )}

          {isTimelineOpen && (
            <div
              className="correios-timeline-backdrop px-3"
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.35)',
                zIndex: 1050,
              }}
            >
              <div
                className="correios-timeline-modal"
                style={{
                  maxWidth: 600,
                  width: '100%',
                  margin: '40px auto',
                  background: '#fff',
                  borderRadius: 8,
                  padding: 20,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Histórico de Rastreamento</h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => setIsTimelineOpen(false)}
                  >
                    Fechar
                  </button>
                </div>

                <p className="mb-3">
                  {/*
                  <a
                    href={correiosLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: 'break-all', display: 'inline-block' }}
                  >
                    {correiosLink}
                  </a>
                  <br />
                  */}
                  <strong>Código:</strong> {tracking.code || '-'}
                </p>

                {!hasEvents && (
                  <p className="text-muted mb-0">
                    Ainda não há eventos registrados pelos Correios para este
                    objeto.
                    <br />O primeiro evento geralmente aparece algumas horas
                    após a postagem.
                  </p>
                )}

                {hasEvents && (
                  <ul className="list-unstyled mt-3 mb-0">
                    {tracking.events
                      .slice()
                      .sort((a, b) => {
                        const da = new Date(
                          a.created_at || a.date || 0
                        ).getTime();
                        const db = new Date(
                          b.created_at || b.date || 0
                        ).getTime();
                        return db - da; // mais recente primeiro
                      })
                      .map((event, index) => (
                        <li
                          key={`${event.created_at || event.date || ''}-${
                            event.description || event.descricao || ''
                          }`}
                          className="mb-3 d-flex"
                        >
                          <div
                            style={{
                              width: 10,
                              marginRight: 10,
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                marginTop: 6,
                                backgroundColor:
                                  index === 0 ? '#0d6efd' : '#ced4da',
                              }}
                            />
                          </div>
                          <div>
                            <div className="small text-muted">
                              {formatDateTime(event.created_at || event.date)}
                            </div>
                            <div className="fw-semibold">
                              {event.description || event.descricao || '-'}
                            </div>
                            {(event.from_city || event.to_city) && (
                              <div className="text-muted small">
                                {event.from_city && event.from_state && (
                                  <>
                                    {event.to_city ? (
                                      <>
                                        de {event.from_type}, {event.from_city}{' '}
                                        - {event.from_state}
                                      </>
                                    ) : (
                                      <>
                                        {event.from_type}, {event.from_city} -{' '}
                                        {event.from_state}
                                      </>
                                    )}
                                    {event.to_city && <br />}
                                  </>
                                )}
                                {event.to_type &&
                                  event.to_city &&
                                  event.to_state && (
                                    <>
                                      para {event.to_type}, {event.to_city} -{' '}
                                      {event.to_state}
                                    </>
                                  )}
                              </div>
                            )}
                            {event.details && (
                              <div className="text-muted small">
                                {event.details}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </Collapse>
      </CardBody>
    </Card>
  );
};
